/* Database A tracking edit — in place; saves to every row of the parcel.
 * Asks the user's own login password in the same box only when the 3-hour edit window is closed. */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";

const useTrackingEdit = (rec, onSaved) => {
  const [stage, setStage] = useState(null); // null | 'pwd' | 'value' | 'busy'
  const [val, setVal] = useState("");
  const ref = useRef(null);
  useEffect(() => { if (stage === "pwd" || stage === "value") { ref.current?.focus(); ref.current?.select(); } }, [stage]);

  const start = async () => {
    if (!rec?.track_no || stage) return;
    try {
      const st = await axios.get(`${API}/database-a/tracking-edit/status`, { headers: getAuthHeader() });
      setVal(st.data?.unlocked ? rec.track_no : "");
      setStage(st.data?.unlocked ? "value" : "pwd");
    } catch { toast.error("Could not check edit permission"); }
  };

  const unlock = async () => {
    if (!val) return toast.error("Password is required");
    setStage("busy");
    try {
      const u = await axios.post(`${API}/database-a/tracking-edit/unlock`, { password: val }, { headers: getAuthHeader() });
      const until = new Date(u.data.unlocked_until).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      toast.success(`Tracking edit unlocked for 3 hours (until ${until})`);
      setVal(rec.track_no); setStage("value");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wrong password");
      setVal(""); setStage("pwd");
    }
  };

  const save = async () => {
    const cleaned = val.replace(/\s+/g, " ").trim().toUpperCase();
    if (!cleaned) return toast.error("Tracking number cannot be empty");
    if (cleaned === rec.track_no.toUpperCase()) { setStage(null); return; }
    setStage("busy");
    try {
      await axios.patch(`${API}/database-a/record`,
        { track_no: rec.track_no, product: rec.product || "", field: "track_no", value: cleaned },
        { headers: getAuthHeader() });
      toast.success(`Tracking updated → ${cleaned} (all rows of this parcel)`);
      onSaved(rec.track_no, cleaned);
      setStage(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Edit failed");
      setStage("value");
    }
  };

  const submit = () => (stage === "pwd" ? unlock() : save());
  const inputProps = {
    ref, value: val, readOnly: stage === "busy",
    type: stage === "pwd" ? "password" : "text",
    autoComplete: stage === "pwd" ? "new-password" : "off",
    placeholder: stage === "pwd" ? "Your login password ↵" : "New tracking ↵",
    onChange: (e) => setVal(e.target.value),
    onKeyDown: (e) => { if (e.key === "Escape") setStage(null); if (e.key === "Enter") submit(); },
  };
  return { stage, start, submit, cancel: () => setStage(null), inputProps };
};

const inputCls = (stage) => `rounded border px-1.5 py-0.5 font-mono focus:outline-none focus:ring-2 ${stage === "pwd" ? "border-amber-400 focus:ring-amber-200" : "border-indigo-400 focus:ring-indigo-200 uppercase"}`;

export const DBATrackingCell = ({ rec, onSaved, testid }) => {
  const t = useTrackingEdit(rec, onSaved);
  return (
    <td className="py-1 px-1.5 text-xs font-mono border border-slate-200 whitespace-nowrap cursor-pointer select-none text-slate-700 hover:bg-amber-50"
      onDoubleClick={(e) => { e.stopPropagation(); t.start(); }}
      title="Double-click to edit tracking — updates every product row of this parcel"
      data-testid={testid}>
      {t.stage ? (
        <input {...t.inputProps} onBlur={() => t.stage !== "busy" && t.cancel()}
          className={`w-32 text-xs ${inputCls(t.stage)}`} data-testid={`${testid}-input`} />
      ) : rec.track_no}
    </td>
  );
};

// Phone sheet: tap "Edit tracking" → box in place with a Save button (keyboards may lack Enter).
export const DBATrackingField = ({ rec, onSaved, autoStart }) => {
  const t = useTrackingEdit(rec, onSaved);
  useEffect(() => { if (autoStart) t.start(); }, [autoStart, rec.id]); // arm once per scanned parcel
  if (!t.stage) return (
    <>
      <span className="font-mono text-slate-700" data-testid="dba-sheet-track">{rec.track_no || "No tracking"}</span>
      {rec.track_no && (
        <button type="button" onClick={t.start} data-testid="dba-sheet-edit-tracking"
          className="h-7 rounded-md border border-slate-300 px-2.5 text-xs font-medium hover:bg-slate-50">Edit tracking</button>
      )}
    </>
  );
  return (
    <div className="flex flex-1 items-center gap-1.5">
      <input {...t.inputProps} className={`flex-1 min-w-0 py-1.5 text-sm ${inputCls(t.stage)}`} data-testid="dba-sheet-track-input" />
      <button type="button" onClick={t.submit} disabled={t.stage === "busy"} data-testid="dba-sheet-track-save"
        className="h-8 rounded-md bg-indigo-600 px-3 text-xs font-semibold text-white disabled:opacity-60">{t.stage === "pwd" ? "Unlock" : "Save"}</button>
      <button type="button" onClick={t.cancel} data-testid="dba-sheet-track-cancel"
        className="h-8 rounded-md border border-slate-300 px-2 text-xs">✕</button>
    </div>
  );
};
